// A room's hidden heater walls (the low wall a heater hides behind, on one of the
// room's edges) and a heater's guard-cover sides: picked, previewed, added and
// removed. docs/claude/rooms.md.
import {
  type HeaterWall,
  type ProductInstance,
  type Vec2,
} from '../../shared/types';
import { traitsOf } from '../../shared/categories';
import { setSide } from './categoryFields';
import {
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  roomsToEdit,
  state,
  vp,
} from './core';
import { wallThicknessOf } from '../dent';
import {
  drawsHeaterBlock,
  HEATER_COVER_DEFAULT,
  heaterCoverBands,
  heaterCoverHit,
  heaterGapEdge,
  heaterGuard,
  type HeaterSide,
  instanceToWorld,
  toInstanceLocal,
} from '../geometry';
import { lightenHex } from '../hexTone';
import {
  heaterWallBands,
  heaterWallsOf,
  maxHeaterWallInset,
} from '../polyRoom';
import { defaultWallColor } from '../render';
import {
  dropFeature,
  featureOf,
} from '../selection';
import {
  type BenchHover,
  regenerateSauna,
  saunaOfHeater,
  selectedInstance,
} from '../main';
import { heaterWallTargetAt } from '../roomTargets';
import {
  type ToolAim,
  wallReach,
} from '../toolAim';

// ---- Hidden heater walls -------------------------------------------------
const HEATER_WALL_DISTANCE = 220;
const HEATER_WALL_HEIGHT = 1000;
const HEATER_WALL_HEIGHT_FROM_GROUND = 600;

/** A hidden heater wall's default colour: the room's wall colour, 20% brighter. */
function heaterWallDefaultColor(room: ProductInstance): string {
  return lightenHex(room.wallColor || defaultWallColor(room), 0.2);
}

/** The band a heater wall on `edge` WOULD occupy, for the green create preview. */
function heaterWallPreviewPoly(room: ProductInstance, edge: number): Vec2[] | null {
  const probe = {
    ...room,
    heaterWalls: [...heaterWallsOf(room), {
      id: '__preview__', edge, distance: HEATER_WALL_DISTANCE,
      height: HEATER_WALL_HEIGHT, heightFromGround: HEATER_WALL_HEIGHT_FROM_GROUND,
      thickness: wallThicknessOf(room), color: heaterWallDefaultColor(room),
    }],
  } as ProductInstance;
  return heaterWallBands(probe).find((b) => b.wall.id === '__preview__')?.poly ?? null;
}

/** Heater-wall tool hover: an existing wall highlights red (click deletes it), a
 *  bare eligible edge previews the new wall in green (click creates it). */
export function heaterWallHoverAt(aim: ToolAim): BenchHover | null {
  const world = aim.world;
  const t = heaterWallTargetAt(roomsToEdit(), world, wallReach(aim));
  if (!t) return null;
  if ('remove' in t) {
    const band = heaterWallBands(t.room).find((b) => b.wall.id === t.remove.id);
    return band ? { instId: t.room.id, polys: [band.poly], kind: 'delete' } : null;
  }
  const poly = heaterWallPreviewPoly(t.room, t.add);
  return poly ? { instId: t.room.id, polys: [poly], kind: 'add' } : null;
}

/** The clearance gap a heater's guard cover sits outside of. */
function heaterCoverGap(inst: ProductInstance): { front?: number; back?: number; left?: number; right?: number } | undefined {
  return heaterGuard(inst).gap;
}

/** Whether this instance draws a guard cover at all — a `guarded` category's
 *  part drawn as a block (a heater), the set the renderer draws one for, so the
 *  hover can never offer a side that is never painted. */
function hasHeaterCoverUI(inst: ProductInstance | null): boolean {
  return !!inst && traitsOf(inst.category).guarded && drawsHeaterBlock(inst);
}

/**
 * Which guard-cover side of the SELECTED heater the cursor offers. Scoped to the
 * selection so a plan full of heaters doesn't light up while the cursor crosses
 * it — the gap box is only drawn at all when the heater is selected.
 */
export function heaterCoverHoverAt(sp: Vec2): { instId: string; side: HeaterSide; kind: 'add' | 'remove' } | null {
  const inst = selectedInstance();
  if (!hasHeaterCoverUI(inst) || state.tool !== 'select') return null;
  const local = toInstanceLocal(inst!, vp.screenToWorld(sp));
  const hit = heaterCoverHit(local, inst!.width, inst!.depth, heaterCoverGap(inst!), inst!.heaterCover, 3 / vp.scale);
  return hit ? { instId: inst!.id, side: hit.side, kind: hit.kind } : null;
}

/** The hovered cover side as world points: a 2-point gap-edge run to stroke, or
 *  the cover band's ring to fill. Derived every frame — nothing is cached. */
export function heaterCoverHoverWorld(): { pts: Vec2[]; kind: 'add' | 'remove' } | null {
  const h = state.heaterCoverHover;
  const inst = h && h.instId === state.selectedId ? state.drawing.instances.find((i) => i.id === h.instId) : null;
  if (!h || !inst) return null;
  const gap = heaterCoverGap(inst);
  if (h.kind === 'add') {
    const seg = heaterGapEdge(h.side, inst.width, inst.depth, gap);
    return { pts: seg.map((p) => instanceToWorld(inst, p)), kind: 'add' };
  }
  const band = heaterCoverBands(inst.heaterCover, inst.width, inst.depth, gap).find((b) => b.side === h.side);
  if (!band) return null;
  const ring = [{ x: band.x0, y: band.y0 }, { x: band.x1, y: band.y0 }, { x: band.x1, y: band.y1 }, { x: band.x0, y: band.y1 }];
  return { pts: ring.map((p) => instanceToWorld(inst, p)), kind: 'remove' };
}

/** Build or clear the guard cover on one side of a heater from its hover click.
 *  Writes through the same `setSide` the properties panel uses, and re-wraps the
 *  HOSTING room's benches — the cover is what they are carved around, not the
 *  heater itself. */
export function applyHeaterCoverSide(instId: string, side: HeaterSide, kind: 'add' | 'remove'): void {
  const inst = state.drawing.instances.find((i) => i.id === instId);
  if (!inst) return;
  pushHistory();
  inst.heaterCover = setSide(inst.heaterCover, side, kind === 'add' ? HEATER_COVER_DEFAULT : 0);
  state.heaterCoverHover = null;
  const room = saunaOfHeater(inst);
  if (room) regenerateSauna(room);
  renderProperties();
  invalidate();
}

/** Create a hidden heater wall on `edge` with the standard defaults. */
export function addHeaterWall(room: ProductInstance, edge: number): HeaterWall {
  // Clamp the defaults for tiny rooms, same rule as the panel edits.
  const cap = maxHeaterWallInset(room, edge);
  const thickness = Math.min(wallThicknessOf(room), Math.max(1, cap));
  const hw: HeaterWall = {
    id: newId('hw'),
    edge,
    distance: Math.min(HEATER_WALL_DISTANCE, Math.max(0, cap - thickness)),
    height: HEATER_WALL_HEIGHT,
    heightFromGround: HEATER_WALL_HEIGHT_FROM_GROUND,
    thickness,
    color: heaterWallDefaultColor(room),
  };
  (room.heaterWalls ??= []).push(hw);
  return hw;
}

/** Remove a heater wall from its room. */
export function removeHeaterWall(room: ProductInstance, wallId: string): void {
  room.heaterWalls = heaterWallsOf(room).filter((h) => h.id !== wallId);
  if (!room.heaterWalls.length) delete room.heaterWalls;
  if (featureOf(state, 'heaterWall')?.wallId === wallId) dropFeature(state, 'heaterWall');
}
